import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  ChartBar,
  ShareNetwork,
  FloppyDisk,
  Copy,
  Check,
  DownloadSimple,
  FileImage,
  FilePdf,
  ArrowLeft,
  Users,
  Heartbeat,
  DotsThree,
} from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/dates";
import { toast } from "sonner";
import { exportAsPNG, exportAsPDF } from "@/lib/exporter";
import { HEALTH_LABEL, HEALTH_DOT } from "@/lib/health";
import NotificationsBell from "@/components/NotificationsBell";

export default function ProjectHeader({
  project,
  role,
  email,
  onUpdate,
  onOpenShare,
  onOpenVersions,
  onSaveVersion,
  exportRef,
  health,
  memberCount,
  onToggleHealth,
}) {
  const [nameEditing, setNameEditing] = useState(false);
  const [name, setName] = useState(project.name);
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);

  const canEdit = role === "admin" || role === "edit";

  const commitName = () => {
    setNameEditing(false);
    if (name.trim() && name !== project.name) {
      onUpdate({ name: name.trim() });
    } else {
      setName(project.name);
    }
  };

  const copyShareLink = () => {
    const url = `${window.location.origin}/project/${project.id}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Share link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExport = async (format) => {
    if (!exportRef?.current) return;
    setExporting(true);
    try {
      if (format === "png") {
        await exportAsPNG(exportRef.current, project.name);
        toast.success("PNG exported");
      } else {
        await exportAsPDF(exportRef.current, project.name);
        toast.success("PDF exported");
      }
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <header className="bg-white border-b border-slate-200 px-4 md:px-6 py-3 md:py-4 shrink-0 z-20" data-testid="project-header">
      <div className="flex items-start justify-between gap-3 md:gap-6">
        <div className="flex items-start gap-2 md:gap-4 min-w-0 flex-1">
          <Link
            to="/dashboard"
            className="h-10 w-10 md:h-11 md:w-11 rounded-md border border-slate-300 bg-white text-slate-600 hover:text-slate-950 hover:border-slate-950 flex items-center justify-center shrink-0"
            title="Back to dashboard"
            data-testid="project-back-btn"
          >
            <ArrowLeft size={18} weight="bold" />
          </Link>
          <div className="h-10 w-10 md:h-11 md:w-11 rounded-md bg-slate-950 text-white flex items-center justify-center shrink-0 hidden sm:flex">
            <ChartBar size={22} weight="fill" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              {nameEditing ? (
                <Input
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={commitName}
                  onKeyDown={(e) => { if (e.key === "Enter") commitName(); if (e.key === "Escape") { setNameEditing(false); setName(project.name); } }}
                  className="h-9 text-xl font-black tracking-tight font-display rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-slate-950"
                  data-testid="project-name-input"
                />
              ) : (
                <h1
                  className={`font-display text-lg md:text-2xl font-black tracking-tight text-slate-950 truncate ${canEdit ? "cursor-text hover:bg-slate-100 px-1 -mx-1 rounded-md" : ""}`}
                  onClick={() => canEdit && setNameEditing(true)}
                  data-testid="project-name"
                >
                  {project.name}
                </h1>
              )}
              <Badge className={`uppercase tracking-widest text-[9px] font-mono-ibm rounded-md border ${role === "admin" ? "bg-slate-950 text-white border-slate-950" : role === "edit" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                {role}
              </Badge>
              {typeof memberCount === "number" && (
                <span
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border bg-slate-50 text-slate-700 border-slate-200 text-[10px] font-mono-ibm uppercase tracking-widest font-medium"
                  title={`${memberCount} collaborator${memberCount === 1 ? "" : "s"} on this project`}
                  data-testid="project-collaborator-count"
                >
                  <Users size={10} weight="bold" />
                  {memberCount}
                </span>
              )}
            </div>
            {/* 4-column metrics: START / GO-LIVE / LAST UPDATED / PROJECT HEALTH.
                Replaces the old inline-chip row to match the canonical design.
                On mobile, gap-x shrinks so labels don't collide. */}
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-4 md:gap-x-8 gap-y-3 max-w-3xl">
              <MetricBlock label="Start Date">
                <DateValue
                  value={project.start_date}
                  editable={canEdit}
                  onChange={(d) => onUpdate({ start_date: d })}
                  testid="header-start-date"
                />
              </MetricBlock>
              <MetricBlock label="Go-Live Target">
                <DateValue
                  value={project.go_live_date}
                  editable={canEdit}
                  onChange={(d) => onUpdate({ go_live_date: d })}
                  testid="header-go-live-date"
                  accent
                />
              </MetricBlock>
              <MetricBlock label="Last Updated">
                <span className="text-sm font-semibold text-slate-900" data-testid="header-last-updated">
                  {relTimeShort(project.last_updated)}
                </span>
              </MetricBlock>
              <MetricBlock label="Project Health">
                {health && project?.health_tracker_enabled !== false ? (
                  <button
                    type="button"
                    onClick={onToggleHealth}
                    className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide hover:opacity-80 transition-opacity"
                    style={{ color: HEALTH_DOT[health.state] }}
                    title={`${health.counts.red} off-track · ${health.counts.yellow} at-risk · ${health.counts.green} healthy. Click to toggle.`}
                    data-testid="project-health-badge"
                  >
                    <span className="h-2 w-2 rounded-full" style={{ background: HEALTH_DOT[health.state] }} />
                    {HEALTH_LABEL[health.state]}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={onToggleHealth}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-950 transition-colors"
                    title="Health tracking is off — click to enable"
                    data-testid="project-health-badge-off"
                  >
                    <Heartbeat size={14} weight="bold" />
                    Off
                  </button>
                )}
              </MetricBlock>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
          <NotificationsBell projectId={project.id} email={email} />
          {/* Desktop: spread the actions. Mobile: collapse into a "⋯ More" menu
              so the header doesn't overflow horizontally. */}
          <div className="hidden md:flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={copyShareLink}
              className="h-9 rounded-md border-slate-300 hover:border-slate-400 text-slate-700 font-medium"
              data-testid="copy-link-btn"
            >
              {copied ? <Check size={14} weight="bold" /> : <Copy size={14} weight="bold" />}
              <span className="ml-2">Copy Link</span>
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={onOpenShare}
              className="h-9 rounded-md border-slate-300 hover:border-slate-400 text-slate-700 font-medium"
              data-testid="share-btn"
            >
              <ShareNetwork size={14} weight="bold" />
              <span className="ml-2">Share</span>
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={exporting}
                  className="h-9 rounded-md border-slate-300 hover:border-slate-400 text-slate-700 font-medium"
                  data-testid="export-btn"
                >
                  <DownloadSimple size={14} weight="bold" />
                  <span className="ml-2">Export</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-md">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest font-mono-ibm text-slate-500">Format</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => handleExport("png")} data-testid="export-png">
                  <FileImage size={14} weight="bold" className="mr-2" /> Export as PNG
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("pdf")} data-testid="export-pdf">
                  <FilePdf size={14} weight="bold" className="mr-2" /> Export as PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {canEdit && (
              <Button
                size="sm"
                onClick={onSaveVersion}
                className="h-9 rounded-md bg-slate-950 hover:bg-slate-800 text-white font-medium"
                data-testid="save-version-btn"
              >
                <FloppyDisk size={14} weight="bold" />
                <span className="ml-2">Save Version</span>
              </Button>
            )}
          </div>

          {/* Mobile-only overflow menu — collapses 4 actions into a single icon */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="md:hidden h-10 w-10 p-0 rounded-md border-slate-300 hover:border-slate-400 text-slate-700"
                data-testid="header-overflow-menu"
                title="More actions"
              >
                <DotsThree size={20} weight="bold" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-md w-56">
              <DropdownMenuItem onClick={copyShareLink} data-testid="mobile-copy-link">
                {copied ? <Check size={14} weight="bold" className="mr-2" /> : <Copy size={14} weight="bold" className="mr-2" />}
                Copy Link
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onOpenShare} data-testid="mobile-share">
                <ShareNetwork size={14} weight="bold" className="mr-2" /> Share
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("png")} data-testid="mobile-export-png">
                <FileImage size={14} weight="bold" className="mr-2" /> Export PNG
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("pdf")} data-testid="mobile-export-pdf">
                <FilePdf size={14} weight="bold" className="mr-2" /> Export PDF
              </DropdownMenuItem>
              {canEdit && (
                <DropdownMenuItem onClick={onSaveVersion} data-testid="mobile-save-version">
                  <FloppyDisk size={14} weight="bold" className="mr-2" /> Save Version
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}

function MetricBlock({ label, children }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest font-semibold font-sf-label text-slate-500 mb-1">
        {label}
      </div>
      <div className="min-w-0 truncate">{children}</div>
    </div>
  );
}

function DateValue({ value, editable, onChange, testid, accent }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  const display = value ? formatDate(value) : "—";
  if (editing) {
    return (
      <input
        type="date"
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => { setEditing(false); if (val !== value) onChange(val || null); }}
        onKeyDown={(e) => { if (e.key === "Enter") { setEditing(false); if (val !== value) onChange(val || null); } if (e.key === "Escape") { setEditing(false); setVal(value || ""); } }}
        className="h-7 px-1 text-sm font-semibold border border-slate-950 rounded-md outline-none"
        data-testid={`${testid}-input`}
      />
    );
  }
  return (
    <button
      type="button"
      className={`text-sm font-semibold tracking-tight text-left ${accent ? "text-emerald-700" : "text-slate-900"} ${editable ? "cursor-pointer hover:underline" : "cursor-default"}`}
      onClick={() => editable && setEditing(true)}
      data-testid={testid}
    >
      {display}
    </button>
  );
}

// Human-readable "Last updated" — "2 hours ago", "yesterday", "Apr 5, 2026".
function relTimeShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return `${m} minute${m === 1 ? "" : "s"} ago`;
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return `${h} hour${h === 1 ? "" : "s"} ago`;
  }
  if (diff < 86400 * 7) {
    const dd = Math.floor(diff / 86400);
    return `${dd} day${dd === 1 ? "" : "s"} ago`;
  }
  return formatDate(iso.slice(0, 10));
}
